import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const RANGES = [
  { key: "month",    label: "This Month" },
  { key: "30d",      label: "Last 30 Days" },
  { key: "lifetime", label: "Lifetime" },
];

function fmtInt(n) {
  if (n == null || Number.isNaN(n)) return "0";
  return Number(n).toLocaleString();
}
function fmtUsd(n) {
  if (n == null || Number.isNaN(n)) return "$0.00";
  return `$${Number(n).toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminAiUsage() {
  const [range, setRange] = useState("month");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get(`/admin/ai-usage/summary?range=${range}`),
        api.get(`/admin/ai-usage/imports?range=${range}&limit=200`),
      ]);
      setSummary(s.data); setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  const avgLabel = `AI Usage — ${RANGES.find(r => r.key === range)?.label || ""}`;
  const anyMeasured = (summary?.extractions_with_usage || 0) > 0;

  return (
    <div data-testid="admin-ai-usage" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl">{avgLabel}</h2>
        <div className="flex items-center gap-2">
          {RANGES.map(r => (
            <button
              key={r.key}
              data-testid={`ai-usage-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`text-xs px-3 h-8 rounded-full border ${range === r.key ? "bg-[#1A1A1A] text-white border-transparent" : "bg-white border-[color:var(--border-strong)] text-[color:var(--text)]"}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ai-usage-cards">
        <Card testid="ai-usage-tokens" label="Tokens Used (measured)" value={fmtInt(summary?.total_tokens)} sub={anyMeasured ? null : "Usage tracking active — no measured extractions in this range yet."} />
        <Card testid="ai-usage-extractions" label="AI Extractions" value={fmtInt(summary?.extractions_total)} />
        <Card testid="ai-usage-cost" label="Estimated Cost" value={fmtUsd(summary?.estimated_cost_usd)} sub="Not billed — Emergent Universal Key is authoritative." />
        <Card testid="ai-usage-avg" label="Average / Extraction" value={fmtUsd(summary?.avg_cost_per_extraction_usd)} sub="Over extractions with measured usage" />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-[color:var(--text-muted)]">
          Model: <span className="font-semibold">{summary?.model || "gemini-3.1-pro-preview"}</span>
          {" · "} Pricing (per 1M tokens): input ${summary?.pricing_per_mtok?.input?.toFixed?.(2) ?? "2.00"} / output ${summary?.pricing_per_mtok?.output?.toFixed?.(2) ?? "12.00"}
        </div>
        <Button data-testid="toggle-ai-usage-details" variant="outline" onClick={() => setShowDetails(v => !v)} className="rounded-lg h-9 text-sm">
          {showDetails ? "Hide details" : "View AI Usage"}
        </Button>
      </div>

      {showDetails && (
        <div data-testid="ai-usage-details" className="overflow-x-auto rounded-xl border border-[color:var(--border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F2EB] text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
              <tr>
                <th className="text-left p-3">Vendor</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Input</th>
                <th className="text-right p-3">Output</th>
                <th className="text-right p-3">Total</th>
                <th className="text-right p-3">Est. Cost</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="p-4 text-center text-[color:var(--text-muted)]">No AI extractions in this range.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.import_id} data-testid={`ai-usage-row-${r.import_id}`} className="border-t border-[color:var(--border)]">
                  <td className="p-3">{r.vendor_name}</td>
                  <td className="p-3 capitalize">{r.vendor_category || "—"}</td>
                  <td className="p-3 capitalize">{r.source || "—"}</td>
                  <td className="p-3">{fmtDate(r.processed_at || r.started_at)}</td>
                  <td className="p-3 text-right">{r.usage_recorded ? fmtInt(r.input_tokens) : <em className="text-[color:var(--text-muted)]">n/a</em>}</td>
                  <td className="p-3 text-right">{r.usage_recorded ? fmtInt(r.output_tokens) : ""}</td>
                  <td className="p-3 text-right font-semibold">{r.usage_recorded ? fmtInt(r.total_tokens) : <span className="text-[10px] text-[color:var(--text-muted)] italic">Usage not recorded</span>}</td>
                  <td className="p-3 text-right">{r.usage_recorded ? fmtUsd(r.estimated_cost_usd) : ""}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                      r.status === "processed" ? "bg-[#E6F6EA] text-[#1A6E3A]" :
                      r.status === "processing" ? "bg-[#FEF3C7] text-[#92400E]" :
                      "bg-[#FDECE6] text-[#B03A00]"
                    }`}>{r.status}</span>
                    {r.status === "failed" && r.error && (
                      <div className="text-[11px] text-[color:var(--accent)] mt-1 max-w-xs truncate" title={r.error}>{r.error}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-[color:var(--text-muted)]">
        Measured usage comes from Gemini responses via the Emergent LLM key. Estimated cost is computed from the pricing above and is <strong>not billed</strong>. Legacy extractions before token instrumentation show &ldquo;Usage not recorded&rdquo;.
      </p>

      <AiDraftMonitoring />
    </div>
  );
}

/* ---------- AI Draft Monitoring section ---------- */
function AiDraftMonitoring() {
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get("/admin/ai-drafts/summary"),
        api.get("/admin/ai-drafts/monitoring"),
      ]);
      setSummary(s.data);
      setVendors(m.data.vendors || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clearVendor = async (row) => {
    if (!window.confirm(
      `Clear ${row.draft_count} AI draft${row.draft_count === 1 ? "" : "s"} for ${row.vendor_name}?\n\nOnly unapproved AI-generated drafts will be deleted. Approved packages and services will NOT be affected.`
    )) return;
    setBusy(row.vendor_id);
    try {
      const { data } = await api.post(`/vendors/${row.vendor_id}/products/drafts/clear`);
      toast.success(`${data.deleted_count} draft${data.deleted_count === 1 ? "" : "s"} removed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear drafts"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-ai-drafts" className="mt-12 space-y-6 border-t border-[color:var(--border)] pt-8">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl">AI Draft Monitoring</h2>
        <button data-testid="ai-drafts-refresh" onClick={load}
          className="text-xs px-3 h-8 rounded-full border bg-white border-[color:var(--border-strong)] text-[color:var(--text)]">
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card testid="ai-drafts-outstanding" label="Outstanding AI Drafts" value={fmtInt(summary?.outstanding_drafts)} />
        <Card testid="ai-drafts-vendors" label="Vendors With Drafts" value={fmtInt(summary?.vendors_with_drafts)} />
        <Card testid="ai-drafts-high" label="High Draft Count (≥ threshold)" value={fmtInt(summary?.high_draft_count_vendors)} sub={`Threshold: ${summary?.threshold ?? 25}`} />
        <Card testid="ai-drafts-oldest" label="Oldest Outstanding Draft" value={fmtDate(summary?.oldest_outstanding_draft_at)} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-[color:var(--border)] bg-white" data-testid="ai-drafts-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F2EB] text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
            <tr>
              <th className="text-left p-3">Vendor</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Drafts</th>
              <th className="text-left p-3">Oldest</th>
              <th className="text-left p-3">Newest</th>
              <th className="text-right p-3">AI Imports</th>
              <th className="text-right p-3">Est. Cost</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.length === 0 && (
              <tr><td colSpan={9} className="p-4 text-center text-[color:var(--text-muted)]">No outstanding AI drafts.</td></tr>
            )}
            {vendors.map(r => (
              <tr key={r.vendor_id} data-testid={`ai-drafts-row-${r.vendor_id}`}
                  className={`border-t border-[color:var(--border)] ${r.flagged ? "bg-[#FFFBEB]" : ""}`}>
                <td className="p-3 font-semibold">{r.vendor_name}</td>
                <td className="p-3 capitalize">{r.vendor_category || "—"}</td>
                <td className="p-3 text-right font-semibold">{fmtInt(r.draft_count)}</td>
                <td className="p-3">{fmtDate(r.oldest_draft_at)}</td>
                <td className="p-3">{fmtDate(r.newest_draft_at)}</td>
                <td className="p-3 text-right">{fmtInt(r.import_count)}</td>
                <td className="p-3 text-right">
                  {r.measured
                    ? fmtUsd(r.estimated_cost_usd)
                    : <span className="text-[10px] italic text-[color:var(--text-muted)]">Usage not recorded</span>}
                </td>
                <td className="p-3">
                  {r.flagged ? (
                    <span data-testid={`ai-drafts-flag-${r.vendor_id}`} className="text-[10px] uppercase tracking-widest bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full">
                      High draft count — review recommended
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">OK</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <Button data-testid={`admin-clear-drafts-${r.vendor_id}`} disabled={busy === r.vendor_id}
                    onClick={() => clearVendor(r)} variant="outline" size="sm"
                    className="h-8 text-xs rounded-lg border-[color:var(--accent)] text-[color:var(--accent)] hover:bg-[color:var(--accent)]/5">
                    {busy === r.vendor_id ? "Working…" : "Clear AI Drafts"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[color:var(--text-muted)]">
        Flagging a vendor is <strong>advisory only</strong>. It does not affect verification, ranking, or publication. Deletion removes only <code>ai_draft=true</code> products; approved packages and food menus are never touched.
      </p>
    </div>
  );
}

function Card({ testid, label, value, sub }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-[color:var(--border)] bg-white p-4">
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mt-1">{label}</div>
      {sub && <div className="text-[11px] text-[color:var(--text-muted)] mt-2">{sub}</div>}
    </div>
  );
}
